/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.24.0
 */

import { ReactElement } from 'react';

import isFunction from 'lodash/isFunction';

import { LinkBlock } from '@/main/components/common';
import { RoutePath } from '@/main/routes/constants/routesConstants';

import { IUserNotificationTranslation } from '../../internationalization/types';
import {
    ENotificationBannerType,
    NOTIFICATIONS_FILTRED_ACCOUNTS_PERSONAL_DATA_LINK,
    NOTIFICATIONS_FILTRED_SUBSCRIBERS_PERSONAL_DATA_LINK,
} from '../constants';
import { INotificationsEntity } from '../types';
import { getNotificationContent } from './commonNotificationsUtils';

type TLinkComponentDataType =
    | {
          href: string;
          text: string;
      }
    | undefined;

export const getBannerNotificationData = (
    entity: INotificationsEntity,
    translation: IUserNotificationTranslation,
    onLinkClick?: () => void,
): [string, ReactElement | undefined] => {
    const content = getNotificationContent(entity);

    const linkData: TLinkComponentDataType = (() => {
        switch (entity.type) {
            case ENotificationBannerType.AUTH:
                return { href: window.location.pathname, text: translation.linkText.bannerAuth };
            case ENotificationBannerType.OUTSTANDING_DEBT:
            case ENotificationBannerType.UNPAID_BILLS:
                return { href: RoutePath.billsUnpaid, text: translation.linkText.default };
            case ENotificationBannerType.PERSONAL_DATA_CONFIRMATION_SUBSCRIBERS:
                return {
                    href: NOTIFICATIONS_FILTRED_SUBSCRIBERS_PERSONAL_DATA_LINK,
                    text: translation.linkText.default,
                };
            case ENotificationBannerType.PERSONAL_DATA_CONFIRMATION_ACCOUNTS:
                return {
                    href: NOTIFICATIONS_FILTRED_ACCOUNTS_PERSONAL_DATA_LINK,
                    text: translation.linkText.default,
                };
            default:
                return undefined;
        }
    })();

    const handleLinkClick = () => {
        if (entity.type === ENotificationBannerType.AUTH) window.location.reload();
        if (isFunction(onLinkClick)) onLinkClick();
    };

    const ItemLink = linkData ? (
        <LinkBlock withArrow url={linkData.href} onClick={handleLinkClick}>
            {linkData.text}
        </LinkBlock>
    ) : undefined;

    return [content, ItemLink];
};
